import { z } from "zod";

// ---- forge: shopper-built boxes -----------------------------------------------------------------
// The rules a forge box is built under, shared by the server (packages/core/src/forge/) and the
// storefront builder, which shows the price live. Ported from boxed-backend's
// `createCommunityBox.ts` and `communityBoxEconomics.ts`; the 5-cent item minimum is Loot OS's own.

/** The share of expected value the house keeps on every forge box, before the creator's cut (boxed's `COMMUNITY_BOX_MARGIN`). */
export const FORGE_MARGIN_PERCENT = 17;
/** Fewest and most entries a forge box's drop table may hold. */
export const FORGE_MIN_ITEMS = 2;
export const FORGE_MAX_ITEMS = 50;
/** The cheapest item a forge box may hold, in US cents: the lowest Mystery Bulk Card tier. */
export const FORGE_MIN_ITEM_VALUE_CENTS = 5;
/** The dearest item a forge box may hold, in US cents (boxed's marketplace-price ceiling). */
export const FORGE_MAX_ITEM_VALUE_CENTS = 5_000_000;
/** Longest forge box name, in characters (boxed's builder). */
export const FORGE_NAME_MAX = 24;
/** The largest cut a tenant may allow a creator, in percent (boxed's ceiling), and in basis points. */
export const FORGE_MAX_CREATOR_CUT_PERCENT = 3;
export const FORGE_MAX_CREATOR_CUT_BPS = FORGE_MAX_CREATOR_CUT_PERCENT * 100;

/**
 * An environment's forge settings, as `storefront_configs.forge_settings` stores them: whether a
 * creator earns a commission, the largest cut one may pick (percent, two decimals at most, since
 * the server keeps basis points), and the preset images a forge box chooses from. Every field
 * defaults, so `{}` is the defaults: commission on, a 3% maximum and no images.
 */
export const forgeSettingsSchema = z.object({
  commissionEnabled: z.boolean().default(true),
  maxCreatorCutPercent: z
    .number()
    .min(0)
    .max(FORGE_MAX_CREATOR_CUT_PERCENT)
    .multipleOf(0.01)
    .default(FORGE_MAX_CREATOR_CUT_PERCENT),
  // http too: local dev serves uploads from http://localhost; a stage's are https.
  boxImages: z
    .array(z.url({ protocol: /^https?$/ }).max(2048))
    .max(100)
    .default([]),
});
export type ForgeSettings = z.infer<typeof forgeSettingsSchema>;

/** The margin a forge box is priced at, in percentage points: the house's 17 plus the creator's cut. */
export function forgeMarginPercent(creatorCutBps: number): number {
  return FORGE_MARGIN_PERCENT + creatorCutBps / 100;
}

/** One entry the forge price is computed over. `id` breaks ties between equal values the way the server orders a table. */
export interface ForgePriceEntry {
  id: string;
  weight: number;
  value: number;
}

/**
 * A forge box's price in US cents: `ceil(EV / (1 − (17 + cut%) / 100))`, boxed's `WEIGHT_TARGET`
 * pricing. It repeats the server's arithmetic step for step (the processor's value-then-id order,
 * the weights normalized by their own sum, `Σ p·v` from zero, the margin divided by 100 before it
 * is taken from 1) so the price a builder shows is the price the box is published at; core's
 * test holds the two equal. The server's price is the one charged. 0 for an empty table.
 */
export function forgeBoxPriceCents(
  entries: readonly ForgePriceEntry[],
  creatorCutBps: number,
): number {
  if (entries.length === 0) return 0;
  const sorted = [...entries].sort((a, b) => a.id.localeCompare(b.id));
  sorted.sort((a, b) => a.value - b.value);
  const totalWeight = sorted.reduce((sum, entry) => sum + entry.weight, 0);
  if (totalWeight <= 0) return 0;
  const ev = sorted.reduce((sum, entry) => sum + (entry.weight / totalWeight) * entry.value, 0);
  const winRate = 1 - forgeMarginPercent(creatorCutBps) / 100;
  return Math.ceil(ev / winRate);
}

/**
 * One member per refusal cause of `forge_drop_table_invalid`, carried in `details.reason` (with
 * `details.itemId` when one entry caused it). `invalid_weight` is a weight that is not a positive
 * integer other than zero, which is `zero_weight`; `weights_sum` a table whose weights do not add
 * up to 1,000,000; `item_not_found` an id that names no catalog item the shopper can see.
 */
export const FORGE_DROP_TABLE_ISSUES = [
  "too_few_items",
  "too_many_items",
  "duplicate_item",
  "invalid_weight",
  "zero_weight",
  "weights_sum",
  "item_not_found",
  "item_not_fulfillable",
  "item_below_minimum",
  "item_above_maximum",
] as const;
export type ForgeDropTableIssue = (typeof FORGE_DROP_TABLE_ISSUES)[number];

/**
 * One member per refusal cause of `forge_cut_invalid`, in `details.reason`: `invalid` is not a
 * whole number of basis points at or above zero, `above_maximum` over the environment's maximum,
 * `commission_disabled` any cut while the environment pays no commission.
 */
export const FORGE_CUT_ISSUES = ["invalid", "above_maximum", "commission_disabled"] as const;
export type ForgeCutIssue = (typeof FORGE_CUT_ISSUES)[number];

/** One member per refusal cause of `forge_name_rejected`, in `details.reason`. */
export const FORGE_NAME_ISSUES = ["empty", "too_long", "special_characters", "profanity"] as const;
export type ForgeNameIssue = (typeof FORGE_NAME_ISSUES)[number];

/** boxed's builder refuses every one of these; spaces are the one separator a name keeps. */
const FORGE_NAME_SPECIAL_CHARACTERS = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/;

/**
 * The first rule a forge box name breaks, checked on the trimmed name, or null. Profanity is the
 * server's alone (the chat filter), so a name this passes can still be refused `profanity`.
 * The server's `parseForgeName` and the storefront builder both ask this.
 */
export function forgeNameIssue(raw: string): Exclude<ForgeNameIssue, "profanity"> | null {
  const name = raw.trim();
  if (name.length === 0) return "empty";
  if (name.length > FORGE_NAME_MAX) return "too_long";
  if (FORGE_NAME_SPECIAL_CHARACTERS.test(name)) return "special_characters";
  return null;
}

/**
 * One member per refusal cause of `forge_image_invalid`, in `details.reason`: `required` is no
 * image while the environment offers some, `not_offered` an image outside its list.
 */
export const FORGE_IMAGE_ISSUES = ["required", "not_offered"] as const;
export type ForgeImageIssue = (typeof FORGE_IMAGE_ISSUES)[number];

// ---- the /v1 wire ----------------------------------------------------------------------------
// The shapes that need `boxSummarySchema` or `ledgerEntrySchema` are in index.ts, beside them.

/** What a shopper sees about the forge box they are looking at: who forged it and their cut. */
export const forgeBoxInfoSchema = z.object({
  /** The creator's username, else `Player XXXX`: the name a battle seat shows. */
  creatorDisplayName: z.string(),
  /** The cut the published version pays its creator, in percent (0–3, two decimals at most). */
  creatorCutPercent: z.number(),
});
export type ForgeBoxInfo = z.infer<typeof forgeBoxInfoSchema>;

/**
 * The forge settings the storefront builder needs, on the resolved storefront config: whether
 * to offer the cut slider at all, and how far it goes. The preset images come from
 * `GET /v1/forge/images`.
 */
export const storefrontForgeSchema = z.object({
  commissionEnabled: z.boolean(),
  maxCreatorCutPercent: z.number(),
});
export type StorefrontForge = z.infer<typeof storefrontForgeSchema>;

/**
 * One row of a forge drop table as the builder sends it: a catalog item from
 * `GET /v1/forge/items` and its weight, parts per 1,000,000. The server checks the table as a
 * whole and names the first cause it refuses (`forge_drop_table_invalid`, `details.reason`).
 */
export const forgeDropTableEntryInputSchema = z.object({
  catalogItemId: z.string().min(1).max(64),
  weight: z.int(),
});
export type ForgeDropTableEntryInput = z.infer<typeof forgeDropTableEntryInputSchema>;

/** A payload bound only; `FORGE_MAX_ITEMS` is the rule, checked with a named reason. */
const forgeDropTableInputSchema = z.array(forgeDropTableEntryInputSchema).max(500);

/** POST /v1/forge/boxes. `imageUrl` "" = the storefront placeholder, allowed only while the site offers no images. */
export const createForgeBoxRequestSchema = z.object({
  name: z.string().max(200),
  imageUrl: z.string().max(2048).default(""),
  /** The creator's cut in percent, two decimals at most; 0 while the site pays no commission. */
  creatorCutPercent: z.number().default(0),
  dropTable: forgeDropTableInputSchema,
});
export type CreateForgeBoxRequest = z.input<typeof createForgeBoxRequestSchema>;

/**
 * PUT /v1/forge/boxes/{boxId}/drop-table: the whole table again, and optionally a new cut (left
 * out = the box's cut in force). `expectedVersion` pins the version the builder loaded; a box
 * that has moved on answers 409 `box_version_changed`.
 */
export const updateForgeDropTableRequestSchema = z.object({
  creatorCutPercent: z.number().optional(),
  dropTable: forgeDropTableInputSchema,
  expectedVersion: z.int().nonnegative().optional(),
});
export type UpdateForgeDropTableRequest = z.infer<typeof updateForgeDropTableRequestSchema>;

/** What the owner may set a forge box to; `deleted` is for good. */
export const FORGE_BOX_STATUS_VALUES = ["active", "archived", "deleted"] as const;
export type ForgeBoxStatusValue = (typeof FORGE_BOX_STATUS_VALUES)[number];

/** PATCH /v1/forge/boxes/{boxId}. */
export const updateForgeBoxRequestSchema = z.object({ status: z.enum(FORGE_BOX_STATUS_VALUES) });
export type UpdateForgeBoxRequest = z.infer<typeof updateForgeBoxRequestSchema>;

/** GET /v1/forge/items: a name search and a value range in US cents over what a forge box may hold. */
export const forgeItemsQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  minCents: z.coerce.number().int().nonnegative().optional(),
  maxCents: z.coerce.number().int().nonnegative().optional(),
  sort: z.enum(["value_asc", "value_desc"]).optional(),
  cursor: z.string().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ForgeItemsQuery = z.input<typeof forgeItemsQuerySchema>;

/** One catalog item a forge box may hold. */
export const forgeItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  imageUrl: z.string(),
  /** US cents. */
  valueCents: z.int(),
  /** One of the four Mystery Bulk Card tiers, which the first page lists before the search. */
  mysteryBulkCard: z.boolean(),
});
export type ForgeItem = z.infer<typeof forgeItemSchema>;

export const forgeItemsResponseSchema = z.object({
  data: z.array(forgeItemSchema),
  nextCursor: z.string().nullable(),
});
export type ForgeItemsResponse = z.infer<typeof forgeItemsResponseSchema>;

/** GET /v1/forge/images: the site's preset box images. `[]` = the builder offers the placeholder alone. */
export const forgeImagesResponseSchema = z.object({ images: z.array(z.string()) });
export type ForgeImagesResponse = z.infer<typeof forgeImagesResponseSchema>;

/** GET /v1/forge/boxes: `new` = newest first, `popular` = most opened first. */
export const FORGE_BOX_SORTS = ["new", "popular"] as const;
export type ForgeBoxSort = (typeof FORGE_BOX_SORTS)[number];

export const forgeBoxesQuerySchema = z.object({
  sort: z.enum(FORGE_BOX_SORTS).default("new"),
  cursor: z.string().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type ForgeBoxesQuery = z.input<typeof forgeBoxesQuerySchema>;
